import { IdxStep, AuthenticatorKey } from '@mgmresorts/mgm-auth-js-sdk';
import { TextInput, TextLink, Alert, Icon } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useContext, useState } from 'react';

import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { usePasskeyEnabled } from '../../../hooks/usePasskeyEnabled';
import { useEvent } from '../../../utils/analytics/useEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { signInWithPasskey } from '../../../utils/auth/passkeySignIn';
import { IDENTITY_WIDGET_PATHS } from '../../../utils/constants';
import { UserAccountContext } from '../../../utils/userAccountContext';
import { Container } from '../Container';
import { PageLayout } from '../PageLayout';

import {
  StyledMailIcon,
  StyledHeading,
  StyledDescription,
  StyledButton,
  StyledResendText,
  StyledEmailDescription,
  StyledAlertContainer,
  StyledSecondaryButton,
} from './styles';

export interface EmailVerificationProps {
  email: string;
  verificationCode: string;
  onVerificationCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerificationCodeBlur?: () => void;
  onSubmit: () => void;
  onResend: () => void;
  onCloseAlert: () => void;
  isLoading: boolean;
  inputError?: boolean;
  inputHint?: string;
  isCountdownRunning: boolean;
  secondsLeft: number;
  submitButtonLabel?: string;
  codeResentSuccessfully?: boolean;
  errorMessage?: string;
  openAlert?: boolean;
  type?: 'enrollment' | 'authentication';
}

export function EmailVerification({
  email,
  verificationCode,
  onVerificationCodeChange,
  onVerificationCodeBlur,
  onSubmit,
  onResend,
  onCloseAlert,
  isLoading,
  inputError = false,
  inputHint = '',
  isCountdownRunning,
  secondsLeft,
  submitButtonLabel,
  codeResentSuccessfully,
  errorMessage,
  openAlert = true,
  type,
}: EmailVerificationProps) {
  const { authClient } = useAuthContext();
  const { hasPhoneEnrolled, hasPasskeyEnrolled, email: userEmail } = useContext(UserAccountContext);
  const { getContentByPath } = useContent();
  const redirectToApp = useAppRedirect();
  const trackEvent = useEvent();
  const { passkeyEnabled } = usePasskeyEnabled();

  // State for passkey signin functionality
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  // Common alert state
  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'error';
    testId: string;
  } | null>(null);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && verificationCode.length === 6 && !isLoading) {
      onSubmit();
    }
  };

  const renderAlerts = () => {
    if (alert) {
      return (
        <Alert
          open={alert != null}
          onClose={() => {
            setAlert(null);
          }}
          variant={alert.variant}
          message={alert.message}
          data-testid={alert.testId}
          role='alert'
        />
      );
    }

    if (codeResentSuccessfully) {
      return (
        <Alert
          open={openAlert}
          onClose={onCloseAlert}
          variant='success'
          message={getContentByPath('mfa.email.codeResentSuccess')}
          data-testid='code-sent-success'
          role='alert'
        />
      );
    }

    if (errorMessage) {
      return (
        <Alert
          open={openAlert}
          onClose={onCloseAlert}
          variant='error'
          message={errorMessage}
          data-testid='code-resent-error'
          role='alert'
        />
      );
    }

    return null;
  };

  const goToSMSVerification = async () => {
    await trackEvent('identity – unified MFA CTAs', 'Text code to your phone', 'unifiedmfactas', {}, true);
    const phoneAuthHandler = authClient.idx.stepHandlers[IdxStep.AuthPhone];
    await phoneAuthHandler.selectAuth(AuthenticatorKey.PHONE_NUMBER);
    navigate(IDENTITY_WIDGET_PATHS.MfaAuthSms, true);
  };

  const handlePasskeySignIn = async () => {
    try {
      setIsPasskeyLoading(true);
      setAlert(null); // Clear any existing alerts

      // Check if passkey feature flag is enabled
      if (!passkeyEnabled) {
        return;
      }

      const result = await signInWithPasskey({
        email: userEmail,
        authClient,
        trackEvent,
        redirectToApp,
      });

      if (!result.success && result.error) {
        onCloseAlert();
        setAlert({
          message: result.error,
          variant: 'error',
          testId: 'passkey-signin-error',
        });
      }
      // If successful, redirectToApp() will be called from the utility
    } catch (error) {
      console.error('Passkey signin error in EmailVerification:', error);
      onCloseAlert();
      setAlert({
        message: 'An unexpected error occurred during passkey signin.',
        variant: 'error',
        testId: 'passkey-signin-error',
      });
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const goToContactSupport = async () => {
    await trackEvent('identity – unified MFA CTAs', "I can't access my account", 'unifiedmfactas', {}, true);
    window.history.replaceState(null, '', '/identity');
    navigate(IDENTITY_WIDGET_PATHS.MfaContactSupport, true);
  };

  return (
    <div>
      <PageLayout
        testId='email-code-entered'
        title={getContentByPath('mfa.email.title')}
        description={getContentByPath('mfa.email.description')}
      >
        <Container height='auto' width='25%'>
          <StyledAlertContainer>{renderAlerts()}</StyledAlertContainer>
          <StyledMailIcon>
            <Icon name='envelope' variant='outlined' size='large' aria-hidden='true' />
          </StyledMailIcon>

          <StyledHeading variant='heading-medium-1' testId='page-title'>
            {getContentByPath('mfa.email.title')}
          </StyledHeading>

          <StyledDescription variant='body-regular-s'>
            {getContentByPath('mfa.email.body')}
            <StyledEmailDescription variant='body-regular-s'>{email}</StyledEmailDescription>
          </StyledDescription>

          <TextInput
            variant='default'
            label={getContentByPath('mfa.email.fields.verificationCode.title')}
            id='verificationCode'
            testId='verification-code'
            disabled={isLoading}
            value={verificationCode}
            onChange={onVerificationCodeChange}
            onBlur={onVerificationCodeBlur}
            onKeyDown={handleKeyDown}
            maxLength={6}
            autoComplete='one-time-code'
            type='text'
            inputMode='numeric'
            error={inputError}
            hint={inputHint}
          />

          <StyledButton
            onClick={onSubmit}
            testId='submit-code'
            type='submit'
            loading={isLoading}
            disabled={isLoading || verificationCode?.length !== 6 || inputError}
            shape='square'
            size='large'
            variant='primary'
            label={submitButtonLabel}
            fullWidth
          />

          {(codeResentSuccessfully || errorMessage || inputError) && (
            <StyledResendText variant='body-regular-m'>
              {isCountdownRunning ? (
                `Resend code available in ${secondsLeft} ${secondsLeft === 1 ? 'second' : 'seconds'}`
              ) : (
                <TextLink button type='button' variant='small' onClick={onResend}>
                  {getContentByPath('mfa.email.resendLink')}
                </TextLink>
              )}
            </StyledResendText>
          )}

          {type === 'authentication' && (
            <>
              {hasPhoneEnrolled && (
                <StyledSecondaryButton
                  onClick={goToSMSVerification}
                  testId='phone-code'
                  type='button'
                  loading={isLoading}
                  disabled={isLoading || isPasskeyLoading}
                  shape='square'
                  size='large'
                  variant='secondary'
                  label={getContentByPath('mfa.email.smsCTA')}
                  fullWidth
                />
              )}
              {passkeyEnabled && hasPasskeyEnrolled && (
                <StyledSecondaryButton
                  onClick={handlePasskeySignIn}
                  testId='passkey-code'
                  type='button'
                  loading={isPasskeyLoading}
                  disabled={isLoading || isPasskeyLoading}
                  shape='square'
                  size='large'
                  variant='secondary'
                  label={getContentByPath('mfa.email.passkeyCTA')}
                  fullWidth
                />
              )}

              <StyledResendText variant='body-regular-m'>
                <TextLink button type='button' variant='large' onClick={goToContactSupport}>
                  {getContentByPath('mfa.email.cantAccessAccountLink')}
                </TextLink>
              </StyledResendText>
            </>
          )}
        </Container>
      </PageLayout>
    </div>
  );
}

EmailVerification.displayName = 'EmailVerification';
